import React, { useEffect, useMemo, useRef } from "react";
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid2 as Grid,
  Paper,
  Box,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import { LoadingDialog } from '../../components/Loading/LoadingDialog';
import { Modal } from '../../components/Modal/Modal';

import EdiOutstandingInvoiceLineItemDetails from '../../components/Invoice/OutstandingEdiInvoiceLineItemDetails';
import { OutstandingEdiInvoiceHeaderDetails } from '../../components/Invoice/OutstandingEdiInvoiceHeaderDetails';
import SnackbarAlert from '../../components/Snackbar/SnackbarAlert';
import { useStyles } from '../../components/Invoice/OustandingEdiInvoiceDetails.styles';
import { receiveStatus } from '../../util/receiveStatus';
import { determineReceiveType, getBasePathForReceiveType, receiveType } from '../../util/receiveTypeMapping';
import { useScan } from '../Receiving/hooks/useScan';
import { useInitialization } from '../Receiving/hooks/useInitialization';
import { useReceiveOperations } from '../Receiving/hooks/useReceiveOperations';
import { usePostReceive } from '../../common/api/usePostReceive';
import { modals } from "../../util/modalUtil";
import { modalClosed } from "../../util/emptyObjectsUtil";
import { getTimestampInLocalTime } from "@/util/dateUtil";

const OutstandingEdiInvoiceDetails: React.FC = () => {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const history = useHistory();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const outstandingEdiInvoiceWelcomeModalShownThisSession = useRef(false);

	const invoiceNumber = queryParams.get("invoiceNumber") || "";
	const purchaseOrderNumber = queryParams.get("purchaseOrderNumber") || "";

	// Use useInitialization to get receive state management and existing receives
	const {
		pageData,
		receive,
		setReceive,
		receives,
		setReceives,
		productDetails,
		loadingDialog,
		setLoadingDialog,
		modal,
		setModal,
		editableInvoiceFields,
		setCurrentScannedProduct,
		activeTab,
		setAlertHeader,
		enabledPriorityStock,
		enabledArrivals,
	} = useInitialization(receiveType.edi, true);

	// Use useReceiveOperations to get onReceivedQuantityChange and onCloseModal
	const { onReceivedQuantityChange, onCloseModal, onClearReceive } = useReceiveOperations(
		activeTab,
		receive,
		setReceive,
		receives,
		setReceives,
		pageData?.purchaseOrder,
		setModal,
		setLoadingDialog,
		pageData?.priorityStock ?? [],
		enabledPriorityStock,
		enabledArrivals,
		setAlertHeader,
		setCurrentScannedProduct
	);

	const invoice = pageData?.invoice;
	const purchaseOrder = pageData?.purchaseOrder;
	const asns = pageData?.advancedShippingNotices || [];

	// Local state for processing and UI
	const [isProcessing, setIsProcessing] = React.useState(false);
	const [snackbar, setSnackbar] = React.useState({
		open: false,
		message: "",
		severity: "info" as "success" | "error" | "info",
	});
	const [isHeaderExpanded, setIsHeaderExpanded] = React.useState(true);
	const [, postReceiveAsync] = usePostReceive(true);

	// Invoice fields for useScan - use from useInitialization or fallback to invoice data
	const invoiceFields = React.useMemo(
		() => ({
			invoiceNumber: editableInvoiceFields.invoiceNumber || invoice?.invoiceNumber || "",
			invoiceDate: editableInvoiceFields.invoiceDate || invoice?.invoiceDate || "",
			receivedDate: undefined,
			notes: editableInvoiceFields.notes || "Outstanding EDI Invoice Receive",
			totalInvoicedFreightEx: editableInvoiceFields.totalInvoicedFreightEx || invoice?.freightCostEx || 0,
			totalInvoicedFreightInc: editableInvoiceFields.totalInvoicedFreightInc || invoice?.freightCostInc || 0,
			totalInvoicedFreightTax: editableInvoiceFields.totalInvoicedFreightTax || invoice?.freightCostTax || 0,
		}),
		[editableInvoiceFields, invoice]
	);

	// Integration with useScan hook
	const { saveQuantity } = useScan({
		purchaseOrder,
		receiveType: receiveType.edi,
		receive,
		productDetails,
		invoiceFields,
		receives,
		setReceives,
		setReceive,
		onReceivedQuantityChange,
		setCurrentScannedProduct,
		setModal,
		onCloseModal,
		setLoadingDialog,
		invoiceLineItems: pageData?.invoice?.lineItems || [],
		initialReceiveStatus: receiveStatus.draft,
		isOutstandingEdiInvoiceReceive: true
	});

	const handleBack = () => {
		history.goBack();
	};

	const handleHeaderExpandClick = (isExpanded: boolean) => {
		setIsHeaderExpanded(isExpanded);
	};

	// Check if all line items meet the requirement (count >= SOH + Invoiced Quantity)
	const allItemsMeetRequirement = React.useMemo(() => {
		if (!invoice?.lineItems || invoice.lineItems.length === 0 || !receive?.receiveLineItems) {
			return false;
		}

		return invoice.lineItems.every((item) => {
			const receiveLineItem = receive.receiveLineItems.find((rli) => rli.sku === item.sku);
			const product = productDetails.find((p) => p.sku === item.sku);
			const count = receiveLineItem?.receivedQuantity || 0;
			const requiredCount = (product?.physicalStockOnHand || 0) + item.quantity;
			return count >= requiredCount && count > 0;
		});
	}, [invoice?.lineItems, productDetails, receive?.receiveLineItems]);

	const proceedToFinalise = async () => {
		if (!receive) {
			setSnackbar({
				open: true,
				message: "No items have been scanned yet.",
				severity: "error",
			});
			return;
		}

		try {
			setIsProcessing(true);

			// Update receive status from Draft to InProgress and set received quantities to invoiced quantities
			const updatedLineItems = receive.receiveLineItems.map((lineItem) => {
				const quantity = lineItem.invoicedQuantity || 0;
				// Find matching PO line item to get unit costs
				const purchaseOrderLineItem = purchaseOrder?.lineItems.find((poItem) => poItem.sku === lineItem.sku);

				return {
					...lineItem,
					receivedQuantity: quantity,
					lineTotalCostEx: purchaseOrderLineItem
						? purchaseOrderLineItem.unitCostEx * quantity
						: lineItem.lineTotalCostEx,
					lineTotalCostTax: purchaseOrderLineItem
						? purchaseOrderLineItem.unitCostTax * quantity
						: lineItem.lineTotalCostTax,
					lineTotalCostInc: purchaseOrderLineItem
						? purchaseOrderLineItem.unitCostInc * quantity
						: lineItem.lineTotalCostInc,
				};
			});

			const totalReceivedQuantity = updatedLineItems.reduce(
				(sum, lineItem) => sum + (lineItem.receivedQuantity || 0),
				0
			);
			const totalInvoicedQuantity = updatedLineItems.reduce(
				(sum, lineItem) => sum + (lineItem.invoicedQuantity || 0),
				0
			);

			const updatedReceive = {
				...receive,
				status: receiveStatus.inProgress,
				receiveLineItems: updatedLineItems,
				totalReceivedQuantity,
				totalInvoicedQuantity,
			};

			await postReceiveAsync(updatedReceive);

			// Redirect to the main Receiving page
			history.push(
				`/receiving?invoiceNumber=${invoiceNumber}&purchaseOrderNumber=${purchaseOrderNumber}&id=${
					receive.id
				}&t=${Date.now()}`
			);
		} catch (error) {
			console.error("Error finalising receive from EDI outstanding invoice:", error);
			setSnackbar({
				open: true,
				message: "Error proceeding to finalise. Please try again.",
				severity: "error",
			});
			setIsProcessing(false);
		}
	};

	// Handle manual count change from UI
	const handleCountChange = React.useCallback(
		(sku: string, count: number) => {
			void saveQuantity(sku, count);
		},
		[saveQuantity]
	);

	useEffect(() => {
		if (
			!pageData ||
			loadingDialog.isOpen ||
			!pageData.matchedReceive ||
			outstandingEdiInvoiceWelcomeModalShownThisSession.current
		) {
			return;
		}

		// Show outdated EDI invoice count modal if matched receive is draft and older than 12 hours
		if (pageData.matchedReceive.status === receiveStatus.draft) {
			// createdTimestamps are in UTC format
			const receiveCreatedTime = new Date(pageData.matchedReceive.createdTimestamp).getTime();
			// currentTime in UTC format
			const currentTime = Date.now();
			const twelveHoursInMilliseconds = 12 * 60 * 60 * 1000;

			if (
				currentTime - receiveCreatedTime >= twelveHoursInMilliseconds &&
				pageData.matchedReceive.receiveLineItems.some((rli) => rli.receivedQuantity > 0)
			) {
				setModal({
					...modals.outdatedEdiInvoiceCountData,
					onPrimaryButtonClick: () => setModal(modalClosed),
					onExitClick: () => setModal(modalClosed),
				});
			}
		}
		// Completed receives imply a disconnect between closing the invoice and finalising the receive, show modal informing user.
		else if (
			pageData.matchedReceive.status === receiveStatus.completed ||
			pageData.matchedReceive.status === receiveStatus.receivedDirectly
		) {
			setModal({
				...modals.outstandingEdiInvoiceHasCompletedReceive,
				onPrimaryButtonClick: () => setModal(modalClosed),
				onExitClick: () => setModal(modalClosed),
			});
		} else {
			// MatchedReceive must therefore be in a status other than Draft and Completed
			const receiveTypeValue = determineReceiveType(
				undefined,
				pageData.matchedReceive.receiveType,
				purchaseOrderNumber,
				invoiceNumber
			);

			// If receiveTypeValue cannot be determined, show modal to clear it before counting.
			if (!receiveTypeValue) {
				setModal({
					...modals.outstandingEdiInvoiceHasInProgressUnknownTypeReceive,
					onPrimaryButtonClick: () => onClearReceive(invoiceNumber),
					onSecondaryButtonClick: () => setModal(modalClosed),
					onExitClick: () => setModal(modalClosed),
				});
			}
			else {
				// Show modal to inform user of existing in-progress receive
				setModal({
					...modals.outstandingEdiInvoiceHasInProgressReceive,
					onPrimaryButtonClick: () =>
						history.push(
							`${getBasePathForReceiveType(receiveTypeValue)}?invoiceNumber=${invoiceNumber}&purchaseOrderNumber=${purchaseOrderNumber}&id=${pageData.matchedReceive?.id}&t=${Date.now()}`
						),
					onSecondaryButtonClick: () => onClearReceive(invoiceNumber),
					onExitClick: () => setModal(modalClosed),
				});
			}
		}

		outstandingEdiInvoiceWelcomeModalShownThisSession.current = true;
	}, [pageData, loadingDialog, setModal, onClearReceive, invoiceNumber, history, purchaseOrderNumber]);

	const receiveComments = useMemo(() => {
		return pageData?.matchedReceive && Array.isArray(pageData?.matchedReceive.comments)
		? pageData?.matchedReceive?.comments
		: [];
	}, [pageData, pageData?.matchedReceive, pageData?.matchedReceive?.comments])

	const purchaseOrderComment = useMemo(() => {
		return purchaseOrder ? {
			id: purchaseOrder.id ?? "",
			receiveId: pageData.matchedReceive?.id ?? "",
			createdTimestamp: getTimestampInLocalTime(purchaseOrder.dateCreated),
			createdByUserId: "",
			createdByUserName: "INTEGRATION JB API",
			text: purchaseOrder.description,
			sourceSystem: "Purchase Order"	
	} : null}, [purchaseOrder]);

	const invoiceComments = useMemo(() => {
		return invoice && Array.isArray(invoice.comments) ? invoice.comments.map((c) => {
			return {
				id: c.id,
				receiveId: pageData?.matchedReceive?.id ?? "",
				createdTimestamp: c.createdTimestamp,
				createdByUserId: c.createdByUserId,
				createdByUserName: c.createdByUserName,
				text: c.text,
				sourceSystem: "Invoice"
			}
		}) : [];
	}, [invoice])

	const concatenatedComments = useMemo(() => {
		return purchaseOrderComment ?
		 	receiveComments.concat([purchaseOrderComment], invoiceComments) 
			: receiveComments.concat(invoiceComments);
	}, [receiveComments, purchaseOrderComment, invoiceComments])


	return (
		<Container className={classes.container}>
			<LoadingDialog isOpen={loadingDialog.isOpen} message={loadingDialog.message || "Loading invoice details..."} />
			<Modal {...modal} />

			<Button startIcon={<ArrowBackIcon />} onClick={handleBack} className={classes.backButton} color="primary">
				Back to Outstanding Invoices
			</Button>
			{invoice && (
				<>
					{/* Header Card with Invoice Summary */}
					<Grid container spacing={3} className={classes.detailsGrid}>
						<Grid size={12}>
							<OutstandingEdiInvoiceHeaderDetails
								invoiceId={invoice.id}
								invoiceNumber={invoice.invoiceNumber}
								purchaseOrderNumber={invoice.purchaseOrderNumber}
								invoiceDate={invoice.invoiceDate}
								purchaseOrderStatus={purchaseOrder?.status}
								supplierName={invoice.vendorName}
								locationId={invoice.locationId}
								locationName={purchaseOrder?.shipToLocation.companyName ?? ""}
								invoiceType="EDI"
								totalPriceInc={invoice.totalCostInc}
								totalFreightInc={invoice.freightCostInc}
								totalQty={invoice.totalQuantity}
								isExpandedHeader={isHeaderExpanded}
								onExpandedButtonClick={handleHeaderExpandClick}
								receiveComments={concatenatedComments}
								asns={asns}
							/>
						</Grid>
					</Grid>

					{/* Line Items with expandable details */}
					<Typography variant="h6" className={classes.heading}>
						EDI Outstanding Items
					</Typography>

					<Paper className={classes.lineItemsContainer}>
						{/* Header Row */}
						<Grid container className={classes.lineItemsHeader}>
							<Grid size={4}>
								<Typography variant="subtitle2">SKU</Typography>
							</Grid>
							<Grid size={2} className={classes.rightAligned}>
								<Typography variant="subtitle2">{isMobile ? "SOH" : "Total SOH"}</Typography>
							</Grid>
							<Grid size={2} className={classes.rightAligned}>
								<Typography variant="subtitle2">{isMobile ? "Qty" : "Invoice Qty"}</Typography>
							</Grid>
							<Grid size={4} className={classes.rightAligned}>
								<Typography variant="subtitle2">Count</Typography>
							</Grid>
						</Grid>
						{invoice.lineItems.map((invoiceLineItem) => {
							const receiveLineItem = receive?.receiveLineItems.find((rli) => rli.sku === invoiceLineItem.sku);
							const currentCount = receiveLineItem?.receivedQuantity || 0;
							const purchaseOrderLineItem = purchaseOrder?.lineItems.find(
								(poItem) => poItem.sku === invoiceLineItem.sku
							);
							const productDetail = productDetails.find((p) => p.sku === invoiceLineItem.sku);
							return (
								<EdiOutstandingInvoiceLineItemDetails
									key={invoiceLineItem.sku}
									invoiceLineItem={invoiceLineItem}
									purchaseOrderLineItem={purchaseOrderLineItem}
									productDetail={productDetail}
									currentCount={currentCount}
									onCountChange={handleCountChange}
								/>
							);
						})}
					</Paper>

					<Box
						mt={3}
						display="flex"
						justifyContent="space-between"
						alignItems="center"
						className={classes.mobileBottomActions}
					>
						{!allItemsMeetRequirement && (
							<Typography variant="body2" color="textSecondary">
								Please count all items to proceed (Counter must be ≥ SOH + Invoiced Qty for each item)
							</Typography>
						)}
						{allItemsMeetRequirement && (
							<Typography variant="body2" style={{ color: "#4caf50", fontWeight: 500 }}>
								✓ All items counted and verified
							</Typography>
						)}
						<Button
							variant="contained"
							color="primary"
							disabled={!allItemsMeetRequirement || isProcessing}
							onClick={proceedToFinalise}
						>
							Proceed to Finalise
						</Button>
					</Box>
				</>
			)}

			{/* Snackbar for feedback */}
			<SnackbarAlert
				open={snackbar.open}
				message={snackbar.message}
				severity={snackbar.severity as any}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			/>
		</Container>
	);
};

export default OutstandingEdiInvoiceDetails;
